import {useAuthState} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import axios from 'axios';
import system from 'helpers/system';
import {useCallback} from 'react';
import {useUpdatedRef} from 'helpers/hooks/utils';

export const apiHost = system.apiHost(system.whiteLabel());
export const apiPath = '/v1';

export const api = axios.create({
  baseURL: apiHost + apiPath,
  headers: {
    'content-type': 'application/json',
    ...(apiHost.includes('ngrok') ? {
      'ngrok-skip-browser-warning': 'true'
    } : null)
  },
});

export function useApi (token) {
  // the atom-backed read, not the context hooks: useApi is reached from ServiceProvider's own
  // Service and Synchronisation, which render above AuthProvider and have no context to read
  const {token: authToken, teamId: authTeamId} = useAuthState();

  // read when the request is sent, not when this callback was created - the token can arrive or
  // be refreshed after react-query has already taken hold of the query function
  const headerRef = useUpdatedRef({
    token: token ?? authToken,
    teamId: authTeamId
  });

  return useCallback((signal = null) => {
    const getPath = (path) => {
      return path.startsWith('/') ? path : ('/' + path);
    };

    const getHeaders = (headers) => {
      return {
        'x-access-token': headerRef.current.token,
        'x-team-id': headerRef.current.teamId,
        ...headers
      };
    }

    return {
      get: (path, params, headers = null) => {
        return api.get(`${getPath(path)}`, {headers: getHeaders(headers), params, signal});
      },
      put: (path, params, data, headers = null) => {
        return api.put(`${getPath(path)}`, data, {headers: getHeaders(headers), params, signal});
      },
      patch: (path, params, data, headers = null) => {
        return api.patch(`${getPath(path)}`, data, {headers: getHeaders(headers), params, signal});
      },
      post: (path, params, data, headers = null) => {
        return api.post(`${getPath(path)}`, data, {headers: getHeaders(headers), params, signal});
      },
      delete: (path, params, data, headers = null) => {
        return api.delete(`${getPath(path)}`, {headers: getHeaders(headers), data, params, signal});
      }
    }
  }, [headerRef]);
}
